/**
 * Colours that are not a design token because they are picked by the data: one
 * per service in the calendar, per tag nobody has named yet, per initial on an
 * avatar. The brand's five come first so the common case stays on-brand.
 */
const colors = [
  "#57BCFF",
  "#9076F7",
  "#C345E9",
  "#F13FAA",
  "#FFA415",
  "#00BFA6",
  "#f13f3f",
  "#FF6B6B", // coral
  "#6BCB77", // green
  "#FFD93D", // yellow
  "#4D96FF", // light blue
  "#A66DD4", // lavender
  "#FF7F50", // salmon
  "#00CED1", // turquoise
  "#FF4500", // bright orange-red
];

export const colorPalette = colors;

/**
 * A stable colour for a string. The only string → colour hash in the app: the
 * badge and the avatar each had their own, so one name came out in two colours.
 * It says nothing about meaning — a status is a `Tone`, never a hash.
 */
export const getColorForString = (str: string): string => {
  str = str || "";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }
  return colors[Math.abs(hash) % colors.length];
};

/**
 * The one vocabulary for "what state is this in", named after the `Badge`
 * colours that draw it. Each maps to a status token, never to a palette class,
 * so a table, a chart and a header agree on what green means.
 */
export type Tone =
  | "blue"
  | "green"
  | "red"
  | "yellow"
  | "orange"
  | "purple"
  | "pink"
  | "gray";

/** The token behind each tone, for what a class cannot reach (SVG, charts). */
export const TONE_VAR: Record<Tone, string> = {
  blue: "--info",
  green: "--success",
  red: "--danger",
  yellow: "--warning",
  orange: "--warning",
  purple: "--purple",
  pink: "--pink",
  gray: "--muted-foreground",
};

/**
 * The tone of a status read off a row, by its value alone — the admin explorer
 * has nothing else to go on, and the tenant screens use the same words for the
 * same states. Undefined for a value that is not a status.
 */
const TONE_BY_STATUS: Record<string, Tone> = {
  ok: "green",
  active: "green",
  success: "green",
  completed: "green",
  connected: "green",
  ready: "green",
  enabled: "green",
  accepted: "green",
  answered: "green",
  error: "red",
  failed: "red",
  failure: "red",
  rejected: "red",
  banned: "red",
  disabled: "red",
  removed: "red",
  cancelled: "red",
  canceled: "red",
  expired: "red",
  pending: "yellow",
  todo: "yellow",
  degraded: "yellow",
  warning: "yellow",
  provisioning: "yellow",
  in_progress: "yellow",
  draft: "blue",
  created: "blue",
  running: "blue",
};

export const statusTone = (value: string): Tone | undefined =>
  TONE_BY_STATUS[value.toLowerCase()];
